/**
 * Layout state hook for managing chat sidebar
 * Handles sidebar width, visibility, and responsive breakpoints
 */

import { useCallback, useEffect, useState } from "react";

interface LayoutState {
  sidebarWidth: number;
  isSidebarOpen: boolean;
  isMobileSheetOpen: boolean;
}

const STORAGE_KEY = "invoices-frontend-layout";
const DEFAULT_WIDTH = 360;
const MIN_WIDTH = 280;
const MAX_WIDTH = 480;
const MOBILE_BREAKPOINT = 1024; // lg breakpoint

export function useLayoutState() {
  const [state, setState] = useState<LayoutState>(() => {
    if (typeof window === "undefined") {
      return { sidebarWidth: DEFAULT_WIDTH, isSidebarOpen: true, isMobileSheetOpen: false };
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Partial<LayoutState>;
        return {
          sidebarWidth: parsed.sidebarWidth ?? DEFAULT_WIDTH,
          isSidebarOpen: parsed.isSidebarOpen ?? true,
          isMobileSheetOpen: false,
        };
      } catch {
        // Invalid JSON, use defaults
      }
    }
    return { sidebarWidth: DEFAULT_WIDTH, isSidebarOpen: true, isMobileSheetOpen: false };
  });

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        sidebarWidth: state.sidebarWidth,
        isSidebarOpen: state.isSidebarOpen,
      }),
    );
  }, [state.sidebarWidth, state.isSidebarOpen]);

  const setSidebarWidth = useCallback((width: number) => {
    setState((prev) => ({
      ...prev,
      sidebarWidth: Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width)),
    }));
  }, []);

  const toggleSidebar = useCallback(() => {
    setState((prev) => ({ ...prev, isSidebarOpen: !prev.isSidebarOpen }));
  }, []);

  const toggleMobileSheet = useCallback(() => {
    setState((prev) => ({ ...prev, isMobileSheetOpen: !prev.isMobileSheetOpen }));
  }, []);

  const closeMobileSheet = useCallback(() => {
    setState((prev) => ({ ...prev, isMobileSheetOpen: false }));
  }, []);

  return {
    ...state,
    isMobile,
    setSidebarWidth,
    toggleSidebar,
    toggleMobileSheet,
    closeMobileSheet,
    MIN_WIDTH,
    MAX_WIDTH,
  };
}
